import React, { Component } from 'react';
import styled from 'styled-components';
import { describeDeviceService } from '../../../services/device-utils-v2.ts';
import { defaultMapMarkerIconForModel } from '../../../services/DeviceMarkerIcons';
import AnimatedText from '../../bits/AnimatedText.tsx';
import { MapMarkerIcon } from '../../bits/MapMarkerIcon.tsx';
import TileActionRowLink from '../../bits/TileActionRowLink.jsx';
import TileV2 from '../../bits/TileV2.tsx';
import DeviceAlertOptionsPanel from './DeviceAlertOptionsPanel.jsx';
import DeviceAudioOverview from './DeviceAudioOverview.jsx';
import { DeviceBatteryModePanel } from './DeviceBatteryModePanel.tsx';
import { DeviceMileagePanel } from './DeviceMileagePanel.tsx';
import { DeviceViewLivePanel } from './DeviceViewLivePanel.tsx';

const MapIconPreview = styled(MapMarkerIcon)`
  height: 44px;
  width: 44px;
`;

const MapIconLabel = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 10px;
`;

class DeviceOptionsSection extends Component {
  renderOptions() {
    const { device } = this.props;
    const { features, activeProfile, userGuideUrl } = this.props.device;
    const options = [];

    const isSuspended = device.state === 'suspended';
    const canExportData = ['active', 'cancel-pending'].includes(device.state) && device.currentSubscription;

    if (isSuspended) {
      options.push(
        <TileV2 key={options.length}>
          <TileActionRowLink
            label={ <AnimatedText label="Reactivate" />}
            to={{
              pathname: `/m/devices/${device.id}/plan`,
              state: { origin: 'device_overview' },
            }}
          />
        </TileV2>
      );
    }
    options.push(<DeviceViewLivePanel device={device} key={options.length} disabled={isSuspended} />);

    if (activeProfile) {
      const effectiveIcon = device.mapMarkerIcon || defaultMapMarkerIconForModel(device.model);
      options.push(
        <TileV2 key={options.length}>
          <TileActionRowLink
            label={(
              <MapIconLabel>
                Map Icon
                <MapIconPreview icon={effectiveIcon} />
              </MapIconLabel>
            )}
            to={{
              pathname: `/m/devices/${device.id}/map-icon`,
              state: { origin: 'device_overview' },
            }}
          />
        </TileV2>,
      );
    }

    // Mileage graph
    if (features.includes('mileage_feature')) {
      options.push(<DeviceMileagePanel key={options.length} device={device} disabled={isSuspended} />);
    }

    // 25Sep24: Not bothering with supporting disabled in the DeviceAudioOverview component
    if (device.audio.enabled) {
      options.push(<DeviceAudioOverview key={options.length} device={device} />);
    }

    if (features.includes('battery_modes_feature')) {
      options.push(
        <DeviceBatteryModePanel key={options.length} device={device} disabled={isSuspended} />
      );
    }

    if (features.includes('trips_feature')) {
      options.push(
        <TileV2 key={options.length} disabled={isSuspended}>
          <TileActionRowLink
            label="Trips"
            to={{
              pathname: '/m/trips',
              search: `?device-filter=${device.id}`,
              state: { origin: 'device_overview' },
            }}
          />
        </TileV2>,
      );
    }

    if (activeProfile) {
      options.push(
        <DeviceAlertOptionsPanel
          key={options.length}
          device={device}
          features={features}
          disabled={isSuspended}
        />,
      );
    }

    const planDescription = device.currentSubscription ? describeDeviceService(device.currentSubscription) : 'None';

    options.push(
      <TileV2 key={options.length}>
        <TileActionRowLink
          label="Service Plan"
          description={planDescription}
          to={{
            pathname: `/m/devices/${device.id}/plan`,
            state: { origin: 'device_overview' },
          }}
        />
      </TileV2>
    );

    options.push(
      <TileV2 key={options.length} disabled={isSuspended}>
        <TileActionRowLink
          label="Activity"
          to={{
            pathname: `/m/devices/${device.id}/activity`,
            state: { origin: 'device_overview' },
          }}
        />
      </TileV2>,
    );

    if (canExportData) {
      options.push(
        <TileV2 key={options.length}>
          <TileActionRowLink
            label="Data Exports"
            to={{
              pathname: `/m/devices/${device.id}/data-exports`,
              state: { origin: 'device_overview' },
            }}
          />
        </TileV2>,
      );
    }

    if (userGuideUrl) {
      options.push(
        <TileV2 key={options.length}>
          <TileActionRowLink
            type="external"
            label="User Guide"
            to="#"
            onClick={this.onDeviceHelpClick}
          />
        </TileV2>,
      );
    }

    return options;
  }

  onDeviceHelpClick = () => {
    const { device } = this.props;
    const url = device.userGuideUrl;

    if (window.cordova) {
      window.cordova.InAppBrowser.open(url, '_system');
    } else {
      window.open(url, '_system');
    }
  };

  render() {
    return (
      <div className="section">
        {this.renderOptions()}
      </div>
    );
  }
}

export default DeviceOptionsSection;
